import { Procedure } from "../../../../types/procedures";
import { procedurePinRank } from "./specialBadge";
import { uploadedImageThumbPath } from "../../../../utils/uploadedImage";
import { useQuote } from "../../QuoteProvider";
import { useGetProceduresByType } from "../../hooks/useGetProceduresByType";
import "./quote-procedures-list.css";
import ProcedureElement from "./ProcedureElement";
import SignatureProcedureCard from "./SignatureProcedureCard";
import TrustBand from "../TrustBand/TrustBand";
// @ts-ignore
import { useTranslation } from "react-i18next";

type QuoteProceduresListFocusedParams = {
  /** the resolved focus shortlist from useCampaignFocus — single source of
   * truth for membership AND order (position-sorted) */
  focusProcedures: Procedure[];
  focusIds: Set<string>;
  setStoryProcedure?: (procedure: Procedure) => void;
};

/**
 * Procedure list for the focused-campaign-mode treatment
 * (StepSelectProcedureV2Treatment). The focus shortlist renders as Signature
 * Cards — hero for single-focus, compact rows for multi-focus. The rest of the
 * catalog always renders in a SEPARATE grid below a divider — separate grids so
 * catalog cards can never flow into the focus rows' empty cells. Control traffic
 * never reaches this component.
 */
const QuoteProceduresListFocused = ({
  focusProcedures,
  focusIds,
  setStoryProcedure,
}: QuoteProceduresListFocusedParams) => {
  const allProcedures = useGetProceduresByType({ procedureType: "all" });
  const {
    selectedProcedures,
    addSelectedProcedure,
    removeSelectedProcedure,
    gender,
  } = useQuote();
  // @ts-ignore
  const { t } = useTranslation("quote");

  const isSingleFocus = focusProcedures.length === 1;

  // The long tail of the catalog, shown by default below the focus shortlist.
  // Specials/featured pinned first (a special/featured pin wins even over the
  // male-only-last rule), then the same male-only-last ordering as
  // QuoteProceduresListAll. (Focus items already render above as Signature
  // Cards; this only orders the catalog tail.)
  const remainingProcedures = [...allProcedures]
    .sort((a: any, b: any) => {
      const aRank = procedurePinRank(a);
      const bRank = procedurePinRank(b);
      if (aRank !== bRank) return aRank - bRank;
      const aIsMaleOnly = a.maleImage && !a.image;
      const bIsMaleOnly = b.maleImage && !b.image;
      if (aIsMaleOnly && !bIsMaleOnly) return 1;
      if (!aIsMaleOnly && bIsMaleOnly) return -1;
      return 0;
    })
    .filter((p: Procedure) => !focusIds.has(p.id));

  const isSelected = (p: Procedure) => selectedProcedures.includes(p);
  const isDisabled = (p: Procedure) =>
    selectedProcedures.some(
      (sel) =>
        sel.incompatibleProcedures?.some((ip) => ip.id === p.id) ||
        p.incompatibleProcedures?.some((ip) => ip.id === sel.id),
    );

  // Catalog tail items use the existing ProcedureElement so the control arm's
  // card UX is identical. Only focus items get Signature Cards.
  const renderCatalogItem = (procedure: Procedure) => {
    const procedureImage =
      gender === "MALE"
        ? procedure.maleImage
        : procedure.image || procedure.maleImage;
    const imageUrl = procedureImage
      ? uploadedImageThumbPath(procedureImage, 240)
      : undefined;

    return (
      <div key={procedure.id}>
        <ProcedureElement
          procedure={procedure}
          isSelected={isSelected(procedure)}
          isDisabled={isDisabled(procedure)}
          removeSelectedProcedure={removeSelectedProcedure}
          addSelectedProcedure={addSelectedProcedure}
          imageUrl={imageUrl}
          setStoryProcedure={setStoryProcedure}
          selectedCount={selectedProcedures.length}
        />
      </div>
    );
  };

  return (
    <>
      {/* ── Focus shortlist: Signature Cards ── */}
      {focusProcedures.map((procedure: Procedure) => (
        <SignatureProcedureCard
          key={procedure.id}
          procedure={procedure}
          isSelected={isSelected(procedure)}
          isDisabled={isDisabled(procedure)}
          variant={isSingleFocus ? "hero" : "row"}
          onSelect={() => addSelectedProcedure(procedure)}
          onDeselect={() => removeSelectedProcedure(procedure)}
        />
      ))}

      {/* ── Surgeon credentials + trust signals, directly under the Signature
           Card(s). Renders on MOBILE too — the only other TrustBand lives in the
           desktop-only QuoteSummary side column, so mobile (88% of high-ticket
           bounce) saw no board-certified / review trust at the decision point. */}
      <div style={{ marginTop: 12, marginBottom: 4 }}>
        <TrustBand />
      </div>

      {/* ── Catalog tail: standard catalog cards in a separate grid ── */}
      {remainingProcedures.length > 0 && (
        <>
          <div className="focus-all-divider">
            {t("procedures.focusAllDivider", {
              defaultValue: "All procedures",
            })}
          </div>
          <div className="procedures-list">
            {remainingProcedures.map((p: Procedure) => renderCatalogItem(p))}
          </div>
        </>
      )}
    </>
  );
};

export default QuoteProceduresListFocused;
